import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { isAuthenticated } from '../lib/auth';
import { AppLayout } from './AppLayout';
import { LoginPage } from '../features/auth/LoginPage';
import { CommandPage } from '../features/command/CommandPage';
import { WorldPage } from '../features/world/WorldPage';
import { MarketsPage } from '../features/markets/MarketsPage';
import { CapitalPage } from '../features/capital/CapitalPage';
import { SecuritiesPage } from '../features/securities/SecuritiesPage';
import { PortfolioPage } from '../features/portfolio/PortfolioPage';
import { ThesesPage } from '../features/theses/ThesesPage';
import { LearningPage } from '../features/learning/LearningPage';
import { GlossaryPage } from '../features/glossary/GlossaryPage';
import { GlossaryEntryPage } from '../features/glossary/GlossaryEntryPage';
import { LibraryPage } from '../features/library/LibraryPage';
import { OperationsPage } from '../features/operations/OperationsPage';
import { DesignSystemPage } from '../routes/design-system';
import { SettingsPage } from '../features/settings/SettingsPage';

function requireAuth() {
  if (!isAuthenticated()) {
    throw redirect({ to: '/' });
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LoginPage,
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/command' });
    }
  },
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: AppLayout,
  beforeLoad: requireAuth,
});

const commandRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/command',
  component: CommandPage,
});

const worldRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/world',
  component: WorldPage,
});

const marketsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/markets',
  component: MarketsPage,
});

const capitalRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/capital',
  component: CapitalPage,
});

const securitiesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/securities/$id',
  component: SecuritiesPage,
});

const portfolioRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/portfolio',
  component: PortfolioPage,
});

const thesesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/theses',
  component: ThesesPage,
});

const learnRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/learn',
  component: LearningPage,
});

const glossaryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/glossary',
  component: GlossaryPage,
});

const glossaryEntryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/glossary/$slug',
  component: GlossaryEntryPage,
});

const libraryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/library',
  component: LibraryPage,
});

const operationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/operations',
  component: OperationsPage,
});

const designRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/design-system',
  component: DesignSystemPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/onboarding',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    commandRoute,
    worldRoute,
    marketsRoute,
    capitalRoute,
    securitiesRoute,
    portfolioRoute,
    thesesRoute,
    learnRoute,
    glossaryRoute,
    glossaryEntryRoute,
    libraryRoute,
    operationsRoute,
    designRoute,
    settingsRoute,
    onboardingRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
